import { useState } from "react";
import { Box, MenuItem, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { FieldWrapper } from "../FieldWrapper";
import { BadgeNumber } from "./BadgeNumber";
import { ButtonRemove } from "./ButtonRemove";
import { FieldQuestion } from "./FieldQuestion";
import { InputMoney } from "./InputMoney";
import { InputStepper } from "./InputStepper";
import {
  CONTROL_WIDTH,
  CUSTOM_SERVICE_CATEGORY,
  getMonthlyQuantity,
  getServiceTotal,
  getServiceUnits,
  parseMoneyAmount,
} from "@helpers/incomeProfileHelpers";
import { formatCurrencyWhole } from "@helpers/funciones";
import { IncomeService } from "@interfaces/incomeProfile";

/** Topes del volumen (notas de la demo de Mercader, 14/oct/2026): sin ellos se podían capturar cifras irreales. */
const MAX_PER_DAY = 99;
const MAX_PER_MONTH = 999;

interface Props {
  service: IncomeService;
  /** Servicios del catálogo (`code` y nombre) para el selector. */
  categories: { code: string; name: string }[];
  /** Número visible de la tarjeta (1, 2, 3…). */
  n: number;
  onChange: (patch: Partial<IncomeService>) => void;
  onRemove: () => void;
  /** La última tarjeta no se puede quitar. */
  canRemove: boolean;
  /** Prefijo de los `data-testid` de sus controles. */
  testId: string;
}

/**
 * Replica `.mp-card` del perfil de ingresos: una tarjeta por servicio con la categoría, las preguntas
 * de volumen en el lenguaje del servicio ("¿cuántas consultas al día?"; las cirugías se preguntan al
 * mes), el precio y, al pie, la cuenta en voz alta con su total mensual.
 */
export const CardService = ({ service, categories, n, onChange, onRemove, canRemove, testId }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const category = getServiceUnits(service.category);
  const categoryName = categories.find((option) => option.code === service.category)?.name ?? "";
  const quantity = getMonthlyQuantity(service);
  const total = getServiceTotal(service);
  const isCustom = service.category === CUSTOM_SERVICE_CATEGORY;
  // "Otro servicio" no deja continuar sin nombre: se avisa desde el inicio y, al salir del campo vacío, como error.
  const [nameTouched, setNameTouched] = useState(false);
  const nameMissing = isCustom && !service.customName.trim();
  const name = isCustom ? service.customName || "tu servicio" : categoryName.toLowerCase();

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "14px",
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      {/* `.mp-card-h`: número, categoría y quitar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          p: "12px 12px 12px 14px",
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "tokens.surface2",
        }}
      >
        <BadgeNumber n={n} />
        <TextField
          size="lendia_input"
          select
          fullWidth
          value={service.category}
          onChange={(e) => onChange({ category: e.target.value })}
          inputProps={{ "data-testid": `${testId}-categoria-select` }}
          sx={{
            bgcolor: "background.paper",
            "& .MuiSelect-select": { fontFamily: "Outfit", fontWeight: 500, color: "primary.dark" },
          }}
        >
          {categories.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
        <ButtonRemove
          label="Quitar servicio"
          onClick={onRemove}
          disabled={!canRemove}
          testId={`${testId}-quitar-button`}
        />
      </Box>

      {isCustom && (
        <Box sx={{ p: "14px 14px 0" }}>
          <FieldWrapper
            label="Nombre del servicio"
            required
            hint={nameMissing && !nameTouched ? "Escribe el nombre del servicio para poder continuar." : undefined}
            error={nameMissing && nameTouched ? "Escribe el nombre del servicio para poder continuar." : ""}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              value={service.customName}
              placeholder="Ej. Aplicación de toxina botulínica"
              onChange={(e) => onChange({ customName: e.target.value })}
              onBlur={() => setNameTouched(true)}
              required
              inputProps={{ maxLength: 120, "aria-required": true, "data-testid": `${testId}-nombre-input` }}
            />
          </FieldWrapper>
        </Box>
      )}

      {/* `.mp-qs`: hasta tres preguntas en fila; en angosto, dos; en móvil, una */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
          gap: "14px",
          p: "16px 14px",
        }}
      >
        {category.monthly ? (
          <FieldQuestion label={`Promedio de ${category.many} al mes`}>
            <InputStepper
              value={service.perMonth}
              onChange={(perMonth) => onChange({ perMonth })}
              min={0}
              max={MAX_PER_MONTH}
              testId={`${testId}-mes-input`}
            />
          </FieldQuestion>
        ) : (
          <>
            <FieldQuestion label={`Promedio de ${category.many} al día`}>
              <InputStepper
                value={service.perDay}
                onChange={(perDay) => onChange({ perDay })}
                min={0}
                max={category.maxPerDay ?? MAX_PER_DAY}
                testId={`${testId}-dia-input`}
              />
            </FieldQuestion>
            <FieldQuestion label={`Días en los que realizas ${category.many} al mes`}>
              <InputStepper
                value={service.daysPerMonth}
                onChange={(daysPerMonth) => onChange({ daysPerMonth })}
                min={1}
                max={31}
                testId={`${testId}-dias-input`}
              />
            </FieldQuestion>
          </>
        )}
        <FieldQuestion label={`Precio por ${category.one}`}>
          <InputMoney
            value={service.price}
            onChange={(price) => onChange({ price })}
            width={isMobile ? "100%" : CONTROL_WIDTH}
            testId={`${testId}-precio-input`}
          />
        </FieldQuestion>
      </Box>

      {/* `.mp-card-f`: la cuenta en voz alta y el total del servicio */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: { xs: 0.5, sm: 1.5 },
          p: "11px 14px",
          borderTop: "1px dashed",
          borderColor: "divider",
          bgcolor: (theme) => `color-mix(in oklab, ${theme.palette.tokens?.surface2} 55%, transparent)`,
        }}
      >
        <Typography variant="body3" color="tokens.textTertiary" sx={{ lineHeight: 1.4 }}>
          {quantity
            ? `${quantity.toLocaleString("en-US")} ${quantity === 1 ? category.one : category.many} al mes × ${formatCurrencyWhole(parseMoneyAmount(service.price))}`
            : `Completa los campos para calcular el ingreso de ${name}`}
        </Typography>
        <Typography
          sx={{
            fontFamily: "Outfit",
            fontSize: 16,
            fontWeight: 600,
            color: "primary.dark",
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
          }}
        >
          {total ? formatCurrencyWhole(total) : "—"}
        </Typography>
      </Box>
    </Box>
  );
};
